import React, { useState, useEffect, useRef, useCallback } from "react";
import { Stage, Layer } from "react-konva";
import IconButton from "@mui/material/IconButton";
import { IoClose } from "react-icons/io5";
import Button from "@mui/material/Button";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ColorPopup from "../ColorPopup";
import Icon from "../Icon";
import { ThemeProvider } from "@mui/material";
import DevelopTheme from "../../DevelopTheme.js";
import { Rnd } from "react-rnd";
import langJSON from "../../../../../langs.json";

import ContentTab from "./ContentTab";

import "./PropertiesMenu.css";
import IconSelection from "../../../../IconSelection.jsx";

function PropertiesMenu({
  points,
  setPoints,
  target, // the selected point whose properties are to be changed
  color, // the color of the icon as of opening this properties menu
  propertiesMenu,
  setPropertiesMenu,
  setListening, // for toggling events on the icons on the page, when the menu is opened / closed
  updatePointProps,
  deleteObject,
  bookId,
}) {
  const [audioToUpload, setAudioToUpload] = useState();
  const [subtitlesToUpload, setSubtitlesToUpload] = useState();
  const [uploading, setUploading] = useState({
    audio: false,
    subtitles: false,
  });

  const [newScale, setNewScale] = useState(target.scale);
  const [colorPickerVisible, setColorPickerVisible] = useState(false);
  const [previewColor, setPreviewColor] = useState(color);
  const colorBoxRef = useRef(); // color preview box
  const hexInputRef = useRef(); // textbox next to color picker
  const [chosenLanguage, setChosenLanguage] = useState("English");
  const [titles, setTitles] = useState(target.title ? target.title : "");
  const [links, setLinks] = useState(target.link ? target.link : "");
  const [audioTracks, setAudioTracks] = useState(target.audioTracks);
  const [subtitles, setSubtitles] = useState(target.subtitles);
  const colorCheckboxRef = useRef();
  const sizeCheckboxRef = useRef();
  const svgUrlRef = useRef();
  const [currentTab, setCurrentTab] = useState("appearance");
  const [iconUrl, setIconUrl] = useState(
    target.iconUrl ||
      "https://d9fedpdsh4r16.cloudfront.net/misc/icon/icono-neutral.svg"
  );

  // TODO: replace this with a proper way to get a list of languages
  const [languages, setLanguages] = useState();

  const [linkText, setLinkText] = useState(links[languages?.[0]] || "");
  const [titleText, setTitleText] = useState(titles[languages?.[0]] || "");

  // update the color of the preview icon as the color picker is manipulated
  const changePickerColor = (color) => {
    setPreviewColor(color.hex);
    hexInputRef.current.value = color.hex.substring(1);
  };

  // this useEffect manages the initial state of the properties menu when opening it
  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    setPreviewColor(target.color); // get the color of the target point when opening the menu
    if (hexInputRef.current)
      hexInputRef.current.value = target.color.substring(1);

    let temp = [];
    langJSON.forEach((lang) => temp.push(lang.name));
    setLanguages(temp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    console.log("CURRENT POINT: ", target);
  }, [target]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter") handleApplyChanges();
  }, []);

  const handleScaleSelect = (e, scale) => {
    setNewScale(e.target.value);
  };
  useEffect(() => {
    console.log("🚀 ~ file: PropertiesMenu.jsx:98 ~ newScale:", newScale);
    updatePointProps({
      ...target,
      scale: newScale,
    });
  }, [newScale]);
  const handleApplyChanges = () => {
    setListening(true);
    updatePointProps({
      ...target,
      scale: newScale,
      color: previewColor,
      title: titles,
      link: links,
      iconUrl: iconUrl,
      audioTracks: audioTracks,
      subtitles: subtitles,
    });
    // if (sizeCheckboxRef.current && sizeCheckboxRef.current.checked) {
    //   setPoints(
    //     points.map((p) => {
    //       return {
    //         ...p,
    //         scale: newScale,
    //       };
    //     })
    //   );
    // }
    if (colorCheckboxRef.current && colorCheckboxRef.current.checked) {
      setPoints(
        points.map((p) => {
          return {
            ...p,
            color: previewColor,
          };
        })
      );
    }
    setPropertiesMenu({
      ...propertiesMenu,
      visible: false,
    });
  };

  return (
    <Rnd
      className={"pip"}
      // bounds="window"
      cancel={".cancel"}
      enableResizing={false}
      default={{
        x: propertiesMenu.x,
        y: propertiesMenu.y,
      }}
    >
      <ThemeProvider theme={DevelopTheme}>
        <div className="propertiesMenu" style={{ backgroundColor: "" }}>
          <div className="propertiesMenu__top">
            <h2>Coaching Point Properties</h2>
            <IconButton
              className="cancel"
              id="closeButton"
              style={{ alignSelf: "flex-start" }}
              disabled={uploading.audio || uploading.subtitles}
              onClick={() => {
                setListening(true);
                setPropertiesMenu({
                  ...propertiesMenu,
                  visible: false,
                });
              }}
            >
              <IoClose />
            </IconButton>
          </div>
          <div
            className="cancel"
            style={{ cursor: "auto", backgroundColor: "#fff" }}
          >
            <div
              className="tab-container"
              style={{
                pointerEvents:
                  uploading.audio || uploading.subtitles ? "none" : "auto",
              }}
            >
              <h5
                className={`section-label ${
                  currentTab === "appearance" ? "selected" : ""
                }`}
                onClick={() => {
                  setCurrentTab("appearance");
                }}
              >
                Appearance
              </h5>
              <h5
                className={`section-label ${
                  currentTab === "content" ? "selected" : ""
                }`}
                style={{
                  pointerEvents:
                    uploading.audio || uploading.subtitles ? "none" : "auto",
                }}
                onClick={() => {
                  setCurrentTab("content");
                }}
              >
                Content
              </h5>
            </div>
            {currentTab === "appearance" && (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    padding: "0.5rem",
                  }}
                >
                  <label style={{ margin: "0 0 0.25rem 0" }} htmlFor="svgLink">
                    <span>Link to .svg</span>
                    <span
                      style={{
                        fontSize: "0.75rem",
                        marginLeft: "1rem",
                        opacity: 0.5,
                      }}
                    >
                      e.g. https://website.com/../cool-icon.svg
                    </span>
                  </label>
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <input
                      ref={svgUrlRef}
                      id="svgLink"
                      type="text"
                      defaultValue={target.iconUrl}
                      style={{ fontSize: "0.75rem", flexGrow: 1 }}
                    />
                    <button
                      className="loadSvgButton"
                      onClick={() => setIconUrl(svgUrlRef.current.value)}
                    >
                      <svg
                        viewBox="0 0 512 512"
                        fill="none"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeMiterlimit="10"
                        strokeWidth="80"
                        height="1rem"
                        width="1rem"
                        style={{ transform: "translateY(-0.1rem)" }}
                      >
                        <path d="M416 128L192 384l-96-96" />
                      </svg>
                    </button>
                  </div>
                  

                  <h2>yesirski</h2>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-around",
                    gap: "0.5rem",
                    padding: "0 0.5rem",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      paddingBottom: "0.5em",
                      flexGrow: 1,
                    }}
                  >
                    {/* <div className="propertiesMenu__prop">
                      <h5>Size</h5>
                      <ToggleButtonGroup
                        exclusive
                        color={"blueButton"}
                        value={newScale}
                        size="small"
                        style={{ height: "75%" }}
                        onChange={handleScaleSelect}
                      >
                        <ToggleButton disabled={newScale === 40} value={40}>
                          S
                        </ToggleButton>
                        <ToggleButton disabled={newScale === 50} value={50}>
                          M
                        </ToggleButton>
                        <ToggleButton disabled={newScale === 60} value={60}>
                          L
                        </ToggleButton>
                      </ToggleButtonGroup>
                      <input type="checkbox" id="sizeCheckbox" ref={sizeCheckboxRef} />
                      <label htmlFor="sizeCheckbox">Apply to all</label>
                    </div> */}
                    <div style={{ display: "flex" }}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                        }}
                      >
                        <div
                          className="popup-properties__property-container"
                          style={{ width: "100%" }}
                        >
                          <p className="popup-properties__property-name">
                            Size
                          </p>
                          <div
                            className="popup-properties__property-options"
                            style={{ width: "100%", gap: "0.5rem" }}
                          >
                            <input
                              type="number"
                              min={20}
                              max={500}
                              value={newScale}
                              onChange={handleScaleSelect}
                              style={{ width: "4rem" }}
                            />
                            <input
                              type="range"
                              min={20}
                              max={500}
                              step={1}
                              value={newScale}
                              onChange={handleScaleSelect}
                              style={{ flexGrow: 1 }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        border: "1px solid lightgray",
                      }}
                    >
                      <div className="propertiesMenu__prop">
                        <h5>Color</h5>
                        <div
                          ref={colorBoxRef}
                          className="colorBox"
                          style={{
                            backgroundColor: previewColor,
                            border: "1px solid #000",
                          }}
                          onClick={(e) => {
                            setColorPickerVisible(true);
                          }}
                        />
                        <label
                          htmlFor="colorTextbox"
                          style={{ margin: "0 0.125em 0 1em" }}
                        >
                          #
                        </label>
                        <input
                          ref={hexInputRef}
                          type="textbox"
                          id="colorTextbox"
                          style={{ width: "4em" }}
                          pattern="^([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$" // validate hex only
                          onChange={() => {
                            setPreviewColor(`#${hexInputRef.current.value}`);
                          }}
                        />
                      </div>
                      <div
                        className="propertiesMenu__prop"
                        style={{ paddingTop: 0 }}
                      >
                        <input
                          type="checkbox"
                          id="colorCheckbox"
                          ref={colorCheckboxRef}
                        />
                        <label htmlFor="colorCheckbox">Apply to all</label>
                      </div>
                    </div>
                  </div>
                  <hr
                    style={{
                      height: "100%",
                      minHeight: "140px",
                      border: 0,
                      borderLeft: "1px solid #ccc",
                      margin: 0,
                    }}
                  />
                  <div className="propertiesMenu__iconArea">
                    <div className="propertiesMenu__prop">
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "0.5rem",
                          padding: "0.5rem",
                        }}
                      >
                        <p style={{ margin: 0 }}>Preview</p>
                        <div className="previewCanvas">
                          <Stage width={74} height={74}>
                            <Layer>
                              <Icon
                                id={target.id}
                                key={target.id}
                                x={37}
                                y={37}
                                // color={previewColor}
                                listening={false}
                                scale={newScale}
                                canvasWidth={74}
                                canvasHeight={74}
                                selected={false}
                                iconUrl={iconUrl}
                              />
                            </Layer>
                          </Stage>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {currentTab === "content" && (
              <ContentTab
                target={target}
                bookId={bookId}
                languages={languages}
                chosenLanguage={chosenLanguage}
                setChosenLanguage={setChosenLanguage}
                links={links}
                setLinks={setLinks}
                linkText={linkText}
                setLinkText={setLinkText}
                titles={titles}
                setTitles={setTitles}
                titleText={titleText}
                setTitleText={setTitleText}
                audioTracks={audioTracks}
                setAudioTracks={setAudioTracks}
                subtitles={subtitles}
                setSubtitles={setSubtitles}
                audioToUpload={audioToUpload}
                setAudioToUpload={setAudioToUpload}
                subtitlesToUpload={subtitlesToUpload}
                setSubtitlesToUpload={setSubtitlesToUpload}
                uploading={uploading}
                setUploading={setUploading}
              />
            )}
          </div>
          <div className="propertiesMenu__bottomButtons cancel">
            <Button
              size="small"
              variant="contained"
              disabled={uploading.audio || uploading.subtitles}
              color={"redButton"}
              onClick={deleteObject}
            >
              Delete
            </Button>
            <div className="propertiesMenu__cancelApply">
              <Button
                size="small"
                color="cancelButton"
                variant="outlined"
                disabled={uploading.audio || uploading.subtitles}
                onClick={() => {
                  setListening(true);
                  setPropertiesMenu({
                    ...propertiesMenu,
                    visible: false,
                  });
                }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={uploading.audio || uploading.subtitles}
                color={"blueButton"}
                onClick={handleApplyChanges}
              >
                <strong>Apply</strong>
              </Button>
            </div>
          </div>
        </div>
        {colorBoxRef.current && (
          <ColorPopup
            open={colorPickerVisible}
            setOpen={setColorPickerVisible}
            anchor={colorBoxRef.current}
            pickerColor={previewColor}
            changePickerColor={changePickerColor}
            pointColorPicker={true}
          />
        )}
      </ThemeProvider>
    </Rnd>
  );
}

export default PropertiesMenu;
